/**
 * Wave 312 · Prumo top-bar pill.
 *
 * Compact entry point: `[mark] Prumo · 3 pendentes`. Click opens the
 * slide-over queue. Cmd-Shift-A also opens it (handled here so any
 * page that mounts the pill gets the shortcut for free).
 *
 * Polling: query auto-refetches every 60s while the pill is mounted.
 * Cheap — the endpoint is server-paginated and the queue is small.
 *
 * Visibility: always rendered, even at 0 pendentes (so the user
 * learns where to find Prumo). Different visual at 0 (subdued ink,
 * no count) vs >0 (brand accent, count visible).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { usePrumo } from '@/store/prumo.store';
import { useT } from '@/hooks/useT';
import { PrumoMark } from './PrumoMark';
import { PrumoQueue } from './PrumoQueue';
import { cn } from '@/lib/cn';

export function PrumoPill({ className }: { className?: string }) {
  const { locale } = useT();
  const isEn = locale === 'en';

  // Wave 351-C · open state moved to the global `usePrumo` store so
  // both the pill and the new `/inbox` route's
  // `callKind: 'open_proposal'` actions can target the same panel
  // without duplicating `useState` or inventing a fake route.
  const isOpen = usePrumo((s) => s.isOpen);
  const openPrumo = usePrumo((s) => s.openPrumo);
  const closePrumo = usePrumo((s) => s.closePrumo);

  // Poll every 60s. We don't expect proposals to land mid-session
  // often (the proposer runs in the daily cron) but a freshly-uploaded
  // NFe or anomaly could materialize one immediately.
  const proposals = useQuery({
    queryKey: ['prumo', 'pill-count'],
    queryFn: () => api.listPrumoProposals({ limit: 50 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const count = proposals.data?.length ?? 0;
  const active = count > 0;

  // Cmd-Shift-A toggle. Mounted at module scope through this hook so
  // any layout that includes <PrumoPill /> gets the shortcut.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault();
        if (isOpen) closePrumo();
        else openPrumo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, openPrumo, closePrumo]);

  return (
    <>
      <button
        type="button"
        onClick={() => openPrumo()}
        title={isEn ? 'Actions · suggested action queue (Cmd+Shift+A)' : 'Ações · fila de ações sugeridas (Cmd+Shift+A)'}
        aria-label={
          count === 0
            ? (isEn ? 'Actions · queue empty' : 'Ações · fila zerada')
            : isEn
              ? `Actions · ${count} ${count === 1 ? 'pending action' : 'pending actions'}`
              : `Ações · ${count} ${count === 1 ? 'ação pendente' : 'ações pendentes'}`
        }
        className={cn(
          'pressable inline-flex h-11 items-center gap-1.5 rounded-md border px-2.5',
          'text-[12px] font-medium transition-colors duration-150',
          // Wave 314O onda D / 315 — anel de foco unificado (.focus-ring).
          'focus-ring',
          active
            ? 'border-brand-500/40 bg-brand-500/5 text-brand-500 hover:bg-brand-500/10'
            : 'border-line bg-surface text-ink-muted hover:bg-surface-2 hover:text-ink',
          className,
        )}
      >
        <PrumoMark size={14} active={active} />
        <span className="tracking-[-0.005em]">{isEn ? 'Actions' : 'Ações'}</span>
        {active && (
          <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded bg-brand-500 px-1 text-[10px] font-semibold tabular-nums text-white">
            {count}
          </span>
        )}
      </button>
      <PrumoQueue />
    </>
  );
}
